import clsx from "clsx";
import DomPurify from "isomorphic-dompurify";
import dynamic from "next/dynamic";
import Image from "next/image";
import { Suspense } from "react";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import LeadForm from "components/Tiles/commonComponents/LeadForm";
import { APP_LANGUAGES } from "utils/language";

const MuxPlayer = dynamic(() => import("components/Tiles/commonComponents/MuxPlayer"));

type GridContactFormProps = {
  bgColor?: string;
  isTall?: boolean;
  title?: string;
  titleTextColor?: string;
  subtitle?: string;
  subtitleTextColor?: string;
  autoPlay?: boolean;
  hasPriority?: boolean;
  useGifThumbnail?: boolean;
  mediaType?: "image" | "video";
  videoId?: string;
  src?: string;
  thumbnailTime?: number;
  formTitle?: string;
  formTitleTextColor?: string;
  visibleFields: {
    name: boolean;
    email: boolean;
    phone: boolean;
    address: boolean;
  };
  redirectToQuiz?: boolean;
  webhookUrl?: string;
  quizSlug?: string;
  buttonText?: string;
  showTermsMessage?: boolean;
  language?: APP_LANGUAGES;
  redirectSlug?: string;
};
const GridContactForm = ({
  bgColor,
  isTall,
  title,
  titleTextColor,
  subtitle,
  subtitleTextColor,
  mediaType,
  videoId,
  thumbnailTime,
  src,
  autoPlay,
  hasPriority,
  useGifThumbnail,
  formTitle,
  formTitleTextColor,
  visibleFields,
  redirectToQuiz,
  webhookUrl,
  quizSlug,
  buttonText,
  showTermsMessage,
  language = APP_LANGUAGES.ENGLISH,
  redirectSlug
}: GridContactFormProps) => {
  return (
    <section id="GridContactForm-135" style={{ backgroundColor: bgColor }} className={clsx("relative @container")}>
      <MaxWidthWrapper
        className={clsx(
          "relative grid w-full grid-cols-1 gap-4 py-8 lg:grid-cols-2 lg:gap-8 lg:py-12",
          isTall ? "min-h-[81vh]" : "min-h-[600px]"
        )}
      >
        <div className="space-y-6">
          {title && (
            <h1
              className="text-balance text-center font-heading text-[42px] leading-[48px] lg:text-start lg:text-[52px] lg:leading-[64px]"
              style={{ color: titleTextColor }}
              dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(title || "") }} // Render the HTML string
            />
          )}

          {!!((src || videoId) && mediaType) && (
            <div className="relative">
              <div className="aspect-square h-[250px] w-full max-w-full md:h-[350px]">
                {!!(mediaType === "image" && src) && (
                  <Image
                    alt="icon"
                    fill
                    className="border-primary-[#E8ECED] z-10 h-full w-full overflow-hidden rounded-xl border object-cover"
                    src={src}
                    sizes="(max-width: 942px) 100vw, 50vw"
                  />
                )}
                {!!(mediaType === "video" && videoId) && (
                  <Suspense>
                    <MuxPlayer
                      playbackId={videoId}
                      thumbnailTime={thumbnailTime}
                      style={
                        {
                          "--center-controls": "block"
                        } as React.CSSProperties
                      }
                      muted={false}
                      className="border-primary-[#E8ECED] z-10 h-full w-full overflow-hidden rounded-3xl object-cover"
                      autoPlay={autoPlay}
                      hasPriority={hasPriority}
                      useGifThumbnail={useGifThumbnail}
                      gestureControl
                    />
                  </Suspense>
                )}
              </div>
            </div>
          )}
          {subtitle && (
            <h2
              className="relative mx-auto max-w-3xl text-center text-subtitle leading-8 lg:text-start"
              style={{ color: subtitleTextColor }}
              dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(subtitle || "") }} // Render the HTML string
            />
          )}
        </div>
        <div className="space-y-3 lg:px-4">
          <h2
            style={{ color: formTitleTextColor }}
            className="tile-main-header text-center font-heading !text-3xlt !leading-[48px] [text-wrap:balance] lg:!leading-[64px]"
          >
            {formTitle}
          </h2>
          <div className="flex flex-col items-center gap-2 lg:gap-3">
            <LeadForm
              visibleFields={visibleFields}
              buttonText={buttonText}
              redirectToQuiz={redirectToQuiz}
              quizSlug={quizSlug}
              webhookUrl={webhookUrl}
              showTermsMessage={showTermsMessage}
              language={language}
              redirectSlug={redirectSlug}
            />
          </div>
        </div>
      </MaxWidthWrapper>
    </section>
  );
};

export default GridContactForm;
